/**
 * Type-aware visual condition ⇄ JS expression compiler (page-builder + workflow
 * layered expressions).
 *
 * Each rule carries a value **type** (string / number / date / boolean / array /
 * object) — operators are scoped to the type (n8n-style), and the type
 * determines the compiled JS: native where the sandbox allows it
 * (`$x === "y"`, `$x > 3`, `String($x).startsWith(...)`, `Object.keys($x).length`)
 * and a shared-stdlib formula where it can't (`MATCHES(...)` for regex,
 * `DATEVAL(...)` for date comparison — host functions that reach RegExp/Date even
 * though the portal interpreter can't, so the SAME expression works in both
 * runtimes — ADR-0018).
 *
 * `compileCondition` produces ONE expression (`braces` wraps it as `{{ }}` for
 * page templates, or raw JS for workflow predicates); `parseCondition` recovers
 * the tree for round-tripping, returning `null` when not representable (the
 * caller then keeps the raw expression and shows the Expression editor).
 *
 * Pure (no React) → unit-tested in `conditionExpr.test.ts`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ValueType = "string" | "number" | "date" | "boolean" | "array" | "object";

export type ConditionOp =
  | "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "between"
  | "contains" | "not_contains" | "starts_with" | "ends_with" | "matches"
  | "after" | "before" | "on_after" | "on_before" | "in_last_days"
  | "is_true" | "is_false"
  | "includes" | "len_eq" | "len_gt" | "has_key"
  | "is_empty" | "is_not_empty" | "exists" | "not_exists";

export interface ConditionRule {
  /** A binding expression, e.g. `$page.stato` or `$json.amount`. */
  field: string;
  /** Value type — scopes the operators + the compiled JS. Optional for
   *  back-compat (inferred from op/value when absent). */
  type?: ValueType;
  op: ConditionOp;
  /** Right-hand operand (ignored for the no-value ops). */
  value: string;
  /** Second operand — only the two-value ops use it (`between`: the upper
   *  bound). */
  value2?: string;
}

export interface ConditionTree {
  combinator: "and" | "or";
  rules: ConditionRule[];
}

interface OpSpec {
  op: ConditionOp;
  label: string;
  needsValue: boolean;
  /** Hint for the value control. `range` → two inputs (min/max). */
  valueKind?: "text" | "number" | "date" | "range";
}

const EMPTY_OPS: OpSpec[] = [
  { op: "is_empty", label: "is empty", needsValue: false },
  { op: "is_not_empty", label: "is not empty", needsValue: false },
];

/** Presence (null/undefined) ops — type-agnostic, offered on every type.
 *  Distinct from is_empty, which also treats `""` / `[]` / `{}` as empty. */
const PRESENCE_OPS: OpSpec[] = [
  { op: "exists", label: "exists", needsValue: false },
  { op: "not_exists", label: "does not exist", needsValue: false },
];

/** Operators offered per value type (n8n-style). */
export const OPS_BY_TYPE: Record<ValueType, OpSpec[]> = {
  string: [
    { op: "eq", label: "is equal to", needsValue: true },
    { op: "neq", label: "is not equal to", needsValue: true },
    { op: "contains", label: "contains", needsValue: true },
    { op: "not_contains", label: "does not contain", needsValue: true },
    { op: "starts_with", label: "starts with", needsValue: true },
    { op: "ends_with", label: "ends with", needsValue: true },
    { op: "matches", label: "matches regex", needsValue: true },
    ...EMPTY_OPS,
    ...PRESENCE_OPS,
  ],
  number: [
    { op: "eq", label: "= equal to", needsValue: true, valueKind: "number" },
    { op: "neq", label: "≠ not equal to", needsValue: true, valueKind: "number" },
    { op: "gt", label: "> greater than", needsValue: true, valueKind: "number" },
    { op: "gte", label: "≥ greater or equal", needsValue: true, valueKind: "number" },
    { op: "lt", label: "< less than", needsValue: true, valueKind: "number" },
    { op: "lte", label: "≤ less or equal", needsValue: true, valueKind: "number" },
    { op: "between", label: "is between", needsValue: true, valueKind: "range" },
    ...EMPTY_OPS,
    ...PRESENCE_OPS,
  ],
  date: [
    { op: "after", label: "is after", needsValue: true, valueKind: "date" },
    { op: "before", label: "is before", needsValue: true, valueKind: "date" },
    { op: "on_after", label: "is on or after", needsValue: true, valueKind: "date" },
    { op: "on_before", label: "is on or before", needsValue: true, valueKind: "date" },
    { op: "in_last_days", label: "is in the last N days", needsValue: true, valueKind: "number" },
    ...EMPTY_OPS,
    ...PRESENCE_OPS,
  ],
  boolean: [
    { op: "is_true", label: "is true", needsValue: false },
    { op: "is_false", label: "is false", needsValue: false },
    ...PRESENCE_OPS,
  ],
  array: [
    { op: "includes", label: "contains", needsValue: true },
    { op: "len_eq", label: "length equals", needsValue: true, valueKind: "number" },
    { op: "len_gt", label: "length greater than", needsValue: true, valueKind: "number" },
    ...EMPTY_OPS,
    ...PRESENCE_OPS,
  ],
  object: [
    { op: "has_key", label: "has key", needsValue: true },
    ...EMPTY_OPS,
    ...PRESENCE_OPS,
  ],
};

export const VALUE_TYPES: { value: ValueType; label: string }[] = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean" },
  { value: "array", label: "List" },
  { value: "object", label: "Object" },
];

export const emptyCondition = (): ConditionTree => ({ combinator: "and", rules: [] });

const isNumeric = (v: string) => v.trim() !== "" && /^-?\d+(\.\d+)?$/.test(v.trim());

/** Resolve a rule's type (explicit, else inferred from op/value — back-compat
 *  for trees authored before types, and for round-tripping). */
export function effectiveType(r: ConditionRule): ValueType {
  if (r.type) return r.type;
  switch (r.op) {
    case "gt": case "gte": case "lt": case "lte": case "between": return "number";
    case "is_true": case "is_false": return "boolean";
    case "after": case "before": case "on_after": case "on_before": case "in_last_days": return "date";
    case "includes": case "len_eq": case "len_gt": return "array";
    case "has_key": return "object";
    case "contains": case "not_contains": case "starts_with": case "ends_with": case "matches":
      return "string";
    case "eq": case "neq": return isNumeric(r.value) ? "number" : "string";
    default: return "string";
  }
}

/** Map an app data-table column (`data_type` + `kind`) to a condition value
 *  type — the SCHEMA-based default for the type chip, preferred over sampling
 *  because it's correct even with no preview rows. `kind` distinguishes
 *  relations: a single `relation` is a scalar id (string), `relation_many` is a
 *  list. Mirrors the backend `DATA_TYPE_SQL` vocabulary
 *  (`hub.apps.schemas`). Unknown → string (the safe default). */
export function columnValueType(
  dataType: string | null | undefined,
  kind?: string | null,
): ValueType {
  if (kind === "relation_many") return "array";
  if (kind === "relation") return "string";
  const t = (dataType ?? "").toLowerCase();
  if (t === "boolean" || t === "bool") return "boolean";
  if (t === "date" || t.includes("timestamp")) return "date";
  if (t === "file" || t.includes("json")) return "object";
  if (
    t.includes("int") ||
    t.includes("numeric") ||
    t.includes("decimal") ||
    t.includes("double") ||
    t.includes("real") ||
    t.includes("float")
  ) {
    return "number";
  }
  return "string"; // text / select / uuid / unknown
}

/** Best-effort value type from a sample value (fallback pre-selection for the
 *  type chip when no schema is available — the user can always override). */
export function inferValueType(v: unknown): ValueType {
  if (typeof v === "number") return "number";
  if (typeof v === "boolean") return "boolean";
  if (Array.isArray(v)) return "array";
  if (v && typeof v === "object") return "object";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}([T ]\d|$)/.test(v)) return "date";
  return "string";
}

/** Operators valid for a rule's type — `[]` for an unknown type. */
export const opsForType = (t: ValueType): OpSpec[] => OPS_BY_TYPE[t] ?? [];
export const opNeedsValue = (t: ValueType, op: ConditionOp): boolean =>
  opsForType(t).find((o) => o.op === op)?.needsValue ?? true;

// ---------------------------------------------------------------------------
// Compile
// ---------------------------------------------------------------------------

const str = (v: string) => JSON.stringify(v);
const numLit = (v: string) => (isNumeric(v) ? v.trim() : "0");
/** numeric → number literal, else quoted string (array `contains` value). */
const anyLit = (v: string) => (isNumeric(v) ? v.trim() : str(v));
const emptyJs = (f: string) => `(${f} === null || ${f} === undefined || ${f} === "")`;
const existsJs = (f: string) => `(${f} !== null && ${f} !== undefined)`;
const notExistsJs = (f: string) => `(${f} === null || ${f} === undefined)`;

function compileRule(r: ConditionRule): string | null {
  const f = r.field.trim();
  if (!f) return null;
  const t = effectiveType(r);
  const v = r.value;
  // Presence ops are type-agnostic (offered on every type) — handle once.
  if (r.op === "exists") return existsJs(f);
  if (r.op === "not_exists") return notExistsJs(f);
  switch (t) {
    case "string":
      switch (r.op) {
        case "eq": return `${f} === ${str(v)}`;
        case "neq": return `${f} !== ${str(v)}`;
        case "contains": return `String(${f}).includes(${str(v)})`;
        case "not_contains": return `!String(${f}).includes(${str(v)})`;
        case "starts_with": return `String(${f}).startsWith(${str(v)})`;
        case "ends_with": return `String(${f}).endsWith(${str(v)})`;
        case "matches": return `MATCHES(${f}, ${str(v)})`;
        case "is_empty": return emptyJs(f);
        case "is_not_empty": return `!${emptyJs(f)}`;
      }
      break;
    case "number":
      switch (r.op) {
        case "eq": return `${f} === ${numLit(v)}`;
        case "neq": return `${f} !== ${numLit(v)}`;
        case "gt": return `${f} > ${numLit(v)}`;
        case "gte": return `${f} >= ${numLit(v)}`;
        case "lt": return `${f} < ${numLit(v)}`;
        case "lte": return `${f} <= ${numLit(v)}`;
        case "between": return `(${f} >= ${numLit(v)} && ${f} <= ${numLit(r.value2 ?? "")})`;
        case "is_empty": return emptyJs(f);
        case "is_not_empty": return `!${emptyJs(f)}`;
      }
      break;
    case "date":
      switch (r.op) {
        case "after": return `DATEVAL(${f}) > DATEVAL(${str(v)})`;
        case "before": return `DATEVAL(${f}) < DATEVAL(${str(v)})`;
        case "on_after": return `DATEVAL(${f}) >= DATEVAL(${str(v)})`;
        case "on_before": return `DATEVAL(${f}) <= DATEVAL(${str(v)})`;
        case "in_last_days": return `DATEVAL(${f}) >= DAYS_AGO(${numLit(v)})`;
        case "is_empty": return emptyJs(f);
        case "is_not_empty": return `!${emptyJs(f)}`;
      }
      break;
    case "boolean":
      if (r.op === "is_true") return `${f} === true`;
      if (r.op === "is_false") return `${f} === false`;
      break;
    case "array":
      switch (r.op) {
        case "includes": return `${f}.includes(${anyLit(v)})`;
        case "len_eq": return `${f}.length === ${numLit(v)}`;
        case "len_gt": return `${f}.length > ${numLit(v)}`;
        case "is_empty": return `(${f} === null || ${f} === undefined || ${f}.length === 0)`;
        case "is_not_empty": return `!(${f} === null || ${f} === undefined || ${f}.length === 0)`;
      }
      break;
    case "object":
      switch (r.op) {
        case "has_key": return `Object.keys(${f}).includes(${str(v)})`;
        case "is_empty": return `(${f} === null || ${f} === undefined || Object.keys(${f}).length === 0)`;
        case "is_not_empty": return `!(${f} === null || ${f} === undefined || Object.keys(${f}).length === 0)`;
      }
      break;
  }
  return null;
}

/** Compile a tree to one expression. `braces` (default true) wraps it as a
 *  `{{ … }}` template (page bindings); `braces: false` returns the RAW JS body
 *  (workflow `filter`/`if` predicates). Empty (no usable rules) → `""`. */
export function compileCondition(tree: ConditionTree, braces = true): string {
  const parts = tree.rules.map(compileRule).filter((p): p is string => !!p);
  if (parts.length === 0) return "";
  const joined = parts.join(tree.combinator === "or" ? " || " : " && ");
  return braces ? `{{ ${joined} }}` : joined;
}

// ---------------------------------------------------------------------------
// Parse (best-effort, recovers the builder's own output incl. the value type)
// ---------------------------------------------------------------------------

function splitTop(body: string, sep: " && " | " || "): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quote = "";
  let cur = "";
  let i = 0;
  while (i < body.length) {
    if (!quote && depth === 0 && body.startsWith(sep, i)) {
      parts.push(cur);
      cur = "";
      i += sep.length;
      continue;
    }
    const c = body[i];
    if (quote) {
      cur += c;
      if (c === "\\" && i + 1 < body.length) {
        cur += body[i + 1];
        i += 2;
        continue;
      }
      if (c === quote) quote = "";
      i++;
      continue;
    }
    if (c === '"' || c === "'") {
      quote = c;
      cur += c;
      i++;
      continue;
    }
    if (c === "(") depth++;
    if (c === ")") depth--;
    cur += c;
    i++;
  }
  parts.push(cur);
  return parts;
}

/** Recover the {value} from a JS literal RHS, or null if not a literal. */
function parseLiteral(rhs: string): string | null {
  const t = rhs.trim();
  if (/^-?\d+(\.\d+)?$/.test(t)) return t;
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    try {
      return JSON.parse(t.startsWith("'") ? `"${t.slice(1, -1)}"` : t);
    } catch {
      return null;
    }
  }
  return null;
}

const rule = (
  field: string,
  type: ValueType,
  op: ConditionOp,
  value = "",
  value2?: string,
): ConditionRule => {
  const r: ConditionRule = { field: field.trim(), type, op, value };
  if (value2 !== undefined) r.value2 = value2;
  return r;
};

const DATE_OP: Record<string, ConditionOp> = { ">": "after", "<": "before", ">=": "on_after", "<=": "on_before" };

function parsePart(raw: string): ConditionRule | null {
  const part = raw.trim();
  let m: RegExpExecArray | null;

  // presence (type-agnostic) — before the is_empty / comparator matchers
  if ((m = /^\((.+?) !== null && \1 !== undefined\)$/.exec(part))) {
    return rule(m[1], "string", "exists");
  }
  if ((m = /^\((.+?) === null \|\| \1 === undefined\)$/.exec(part))) {
    return rule(m[1], "string", "not_exists");
  }
  // string: matches regex
  if ((m = /^MATCHES\((.+),\s*(.+)\)$/.exec(part))) {
    const v = parseLiteral(m[2]);
    return v !== null ? rule(m[1], "string", "matches", v) : null;
  }
  // date: in the last N days (DAYS_AGO) — before the DATEVAL-both matcher
  if ((m = /^DATEVAL\((.+)\) >= DAYS_AGO\((.+)\)$/.exec(part))) {
    const v = parseLiteral(m[2]);
    return v !== null ? rule(m[1], "date", "in_last_days", v) : null;
  }
  // number: between (two bounds)
  if ((m = /^\((.+?) >= (.+?) && \1 <= (.+?)\)$/.exec(part))) {
    const lo = parseLiteral(m[2]);
    const hi = parseLiteral(m[3]);
    return lo !== null && hi !== null ? rule(m[1], "number", "between", lo, hi) : null;
  }
  // date comparisons
  if ((m = /^DATEVAL\((.+)\)\s*(>=|<=|>|<)\s*DATEVAL\((.+)\)$/.exec(part))) {
    const v = parseLiteral(m[3]);
    return v !== null ? rule(m[1], "date", DATE_OP[m[2]], v) : null;
  }
  // object empty / not-empty
  if ((m = /^!?\((.+?) === null \|\| \1 === undefined \|\| Object\.keys\(\1\)\.length === 0\)$/.exec(part))) {
    return rule(m[1], "object", part.startsWith("!") ? "is_not_empty" : "is_empty");
  }
  // object has_key
  if ((m = /^Object\.keys\((.+)\)\.includes\((.+)\)$/.exec(part))) {
    const v = parseLiteral(m[2]);
    return v !== null ? rule(m[1], "object", "has_key", v) : null;
  }
  // array empty / not-empty
  if ((m = /^!?\((.+?) === null \|\| \1 === undefined \|\| \1\.length === 0\)$/.exec(part))) {
    return rule(m[1], "array", part.startsWith("!") ? "is_not_empty" : "is_empty");
  }
  // array length
  if ((m = /^(.+)\.length (===|>) (.+)$/.exec(part))) {
    const v = parseLiteral(m[3]);
    if (v !== null) return rule(m[1], "array", m[2] === "===" ? "len_eq" : "len_gt", v);
  }
  // string starts/ends with
  if ((m = /^String\((.+)\)\.(startsWith|endsWith)\((.+)\)$/.exec(part))) {
    const v = parseLiteral(m[3]);
    return v !== null ? rule(m[1], "string", m[2] === "startsWith" ? "starts_with" : "ends_with", v) : null;
  }
  // string contains / not-contains
  if ((m = /^(!?)String\((.+)\)\.includes\((.+)\)$/.exec(part))) {
    const v = parseLiteral(m[3]);
    return v !== null ? rule(m[2], "string", m[1] ? "not_contains" : "contains", v) : null;
  }
  // generic empty / not-empty (string / number / date — cosmetic type)
  if ((m = /^!?\((.+?) === null \|\| \1 === undefined \|\| \1 === ""\)$/.exec(part))) {
    return rule(m[1], "string", part.startsWith("!") ? "is_not_empty" : "is_empty");
  }
  // array contains (no String/Object wrapper)
  if ((m = /^(.+)\.includes\((.+)\)$/.exec(part))) {
    const field = m[1].trim();
    if (!field.startsWith("String(") && !field.startsWith("Object.keys(")) {
      const v = parseLiteral(m[2]);
      if (v !== null) return rule(field, "array", "includes", v);
    }
  }
  // comparators (eq/neq/number/boolean)
  if ((m = /^(.+?) (===|!==|>=|<=|>|<) (.+)$/.exec(part))) {
    const field = m[1].trim();
    const sym = m[2];
    const rhs = m[3].trim();
    if (sym === "===" && rhs === "true") return rule(field, "boolean", "is_true");
    if (sym === "===" && rhs === "false") return rule(field, "boolean", "is_false");
    const v = parseLiteral(rhs);
    if (v === null) return null;
    const num = /^-?\d+(\.\d+)?$/.test(rhs);
    if (sym === "===") return rule(field, num ? "number" : "string", "eq", v);
    if (sym === "!==") return rule(field, num ? "number" : "string", "neq", v);
    const cmp: Record<string, ConditionOp> = { ">": "gt", ">=": "gte", "<": "lt", "<=": "lte" };
    return rule(field, "number", cmp[sym], v);
  }
  return null;
}

function parseBody(body: string): ConditionTree | null {
  if (body === "") return emptyCondition();
  const hasAnd = splitTop(body, " && ").length > 1;
  const hasOr = splitTop(body, " || ").length > 1;
  if (hasAnd && hasOr) return null; // mixed precedence — not representable in v1
  const combinator: "and" | "or" = hasOr ? "or" : "and";
  const parts = splitTop(body, hasOr ? " || " : " && ");
  const rules: ConditionRule[] = [];
  for (const p of parts) {
    const r = parsePart(p);
    if (!r) return null;
    rules.push(r);
  }
  return { combinator, rules };
}

/**
 * Parse an expression back into a condition tree. `braces` (default true)
 * expects a `{{ … }}` template (page bindings); `braces: false` parses a RAW
 * expression (workflow predicates). Returns `null` when not representable.
 */
export function parseCondition(
  expr: string | null | undefined,
  braces = true,
): ConditionTree | null {
  if (expr == null || expr.trim() === "") return emptyCondition();
  let body: string;
  if (braces) {
    const m = /^\{\{\s*([\s\S]*?)\s*\}\}$/.exec(expr.trim());
    if (!m) return null;
    body = m[1].trim();
  } else {
    body = expr.trim();
  }
  return parseBody(body);
}
